import hash from 'object-hash';
import debounce from 'debounce';
import { trackEventSchema } from './schema';
import { isInsideAppContainer } from '../../../global-utils/vmFunctions/others';
import { isArr } from '../../../global-utils';
import { getIsAdminPreview } from '../../../shared/admin-preview';
import { emptyNull } from '../../../shared/misc';
import storageAndCookie from '../../../global-utils/storage-and-cookies';
import { getSettingsById, isActiveById } from '../../utils';
import { MODULE_NAME } from './constants';

/* tracks is a map of the debounced functions per `detail` object.
 keys are hashed detail objects (important that before hash they are sorted)
 values are debounced-dispatch track functions https://www.npmjs.com/package/debounce.
 Once there is a request to track event, we save debounced-dispatchTrack to Map
 After debounceInterval, dispatchTrack gets called and then the value
 for is clear from Map. */
const tracks = new Map();

export const getSettings = () => getSettingsById(MODULE_NAME);
export const getIsActive = () => isActiveById(MODULE_NAME);

const dispatchTrack = (h, detail) => () => {
  if (getIsAdminPreview()) return;
  if (!window.DataInsightTracking?.trackingListener) return;
  const ev = new CustomEvent('data-insight.track', { detail });
  window.dispatchEvent(ev);
  tracks.delete(h);
};

export const dataInsightTrack = (params) => {
  const config = getSettings();
  if (!config) return; // Module was not initialized

  // detail - set of properties passed to tracking event
  const { debounceInterval = config.debounceInterval, ...detail } = params;
  const { success, data, error } = trackEventSchema.safeParse(detail);

  if (!success) {
    console.log(error.format());
    throw new Error('data-insight: wrong parametrization', {
      cause: detail
    });
  }

  const h = hash(detail);
  if (!tracks.has(h)) {
    tracks.set(h, debounce(dispatchTrack(h, data), debounceInterval));
  }

  tracks.get(h)();
};

export const trackPage = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page', ...params });
export const trackPageProgram = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page-program', ...params });
export const trackPageProfile = (params) => dataInsightTrack({ sourceApplicationAction: 'vmx-page-profile', ...params });

export const dataInsightInit = (user = emptyNull) => {
  const { authKey: dataInsightAuthKey, trackImportId } = getSettings();
  const detail = {
    dataInsightAuthKey,
    user: {
      ...user,
      EventDevice: isInsideAppContainer() ? 'vmx-mobile-app' : null
    }
  };

  // PAG expects the ImportId in cookie:
  if (trackImportId) {
    storageAndCookie.set('sso_id', user.UserExternalId ?? null, null, true);
  }

  const ev = new CustomEvent('data-insight.init', { detail });
  window.dispatchEvent(ev);
};

const getFromBookedTickets = (bookedTickets) => bookedTickets.find((ticket) => !!ticket.ParticipantTypeName)?.ParticipantTypeName ?? null;

const resolveUserExternalId = (extendedUser) => {
  // if ImportId is undefined coerce to null
  const config = getSettings();
  const { ImportId = null } = extendedUser;

  if (config.trackImportId) return ImportId;

  // if switch is false, set to null
  return null;
};

// To initialize data-insight, we need some additional information about user:
// https://jmarquardt.atlassian.net/browse/MEV-3917
export const extendUser = ({
  userLoginInfo = emptyNull,
  extendedUser = emptyNull,
  bookedTickets // useBookedTickets call always returns at least empty arrays
}) => ({
  ...extendedUser,
  UserIsHcp: extendedUser.HealthCareProfessional,
  UserAreaOfInterest: isArr(extendedUser.SelectedInterests)
    ? extendedUser.SelectedInterests.join('|')
    : null,
  UserGender: extendedUser.GenderExtended,
  UserProfession: extendedUser.Profession,
  UserVisitorType: extendedUser.ParticipantTypeName ?? getFromBookedTickets(bookedTickets),
  UserId: userLoginInfo.user?.partner_id,
  UserCity: userLoginInfo.user?.city,
  UserCountryCode: userLoginInfo.user?.country,
  UserExternalId: resolveUserExternalId(extendedUser)
});
